import { useState, useEffect, useRef, useCallback } from 'react';
import Table from '../../components/Table';
import { Grid, IconButton, Tooltip } from '@mui/material';
import { authPostRequest } from 'Api/api';
import { useLocation, useNavigate } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { style } from 'constant/constants';
import AddIcon from '@mui/icons-material/Add';
import Loader from 'components/Loader';
import { tableOptionFilter, columOptions, isUser, isAdmin } from '../../utils/utils';
import TextEllipsize from 'utils/TextEllipsize';
import DialogBox from 'components/DialogBox';
import DeleteSpan from 'components/DeleteSpan';
import SuccessError from 'components/SuccessError';
import CustomButton from 'components/Button';
import EditIcon from '@mui/icons-material/Edit';
import { dateFormatter } from 'utils/dateFormatter';
import CreateRoofSection from './CreateRoofSection';
import CreateProject from 'pages/projects/CreateProject';
import BackButton from 'components/BackButton';
import PriceGrid from 'components/PriceGrid';
import { generateInvoicePdf } from 'pages/roofSection/generateInvoicPdf';
import InvoicePDF from 'components/InvoicePDF';
import { EmailOutlined, Download } from '@mui/icons-material/index';
import ToggleOffIcon from '@mui/icons-material/ToggleOff';
import ToggleOnIcon from '@mui/icons-material/ToggleOn';
import ConfirmationBox from 'components/ConfirmationBox';
import TextHighlightSpan from 'components/TextHighlightSpan';
import SimCardDownloadIcon from '@mui/icons-material/SimCardDownload';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { addReadOnly, removeReadOnly } from 'reduxStore/authSlice';
import ShowDataWithSpaces from 'components/ShowDataWithSpaces';
import ProjectDetailsCard from './ProjectDetialsCard';
import { getStoreLogoPreviewUrl } from '../storePages/storeUtils';
import {
  downloadPDF,
  downloadSingleQuotePDF,
  getFormattedQuoteDataForPDF,
  sendInvoiceViaEmail
} from './RoofSectionHelper';

const isSort = {
  sort: false,
  ...columOptions
};
const columns_optionsIDs = { filter: false, display: 'excluded' };

const columns = [
  {
    name: 'id',
    label: 'ID',
    options: columns_optionsIDs
  },
  {
    name: 'name',
    label: 'Name',
    options: {
      ...isSort,
      customBodyRender: (value) => <ShowDataWithSpaces value={value} />
    }
  },
  {
    name: 'description',
    label: 'Description',
    options: {
      ...isSort,
      customBodyRender: (value) => <ShowDataWithSpaces value={value} />
    }
  },
  {
    name: 'quotationType',
    label: 'Type',
    options: {
      ...isSort,
      customBodyRender: (value) => <TextEllipsize value={value} />
    }
  },
  {
    name: 'price',
    label: 'Price ($)',
    options: {
      ...isSort,
      customBodyRender: (value) => value && parseFloat(value).toFixed(2)
    }
  },
  {
    name: 'addDate',
    label: 'Add Date',
    options: {
      ...isSort,
      customBodyRender: (value) => dateFormatter(value)
    }
  }
];

const RoofSections = () => {
  const location = useLocation();
  const navigate = useNavigate();
  let projectState = location?.state;
  const projects = projectState?.index;
  const projectID = projects?.id;
  const projectName = projects?.name;
  const projectDescription = projects?.description;

  const [roofTypes, setRoofTypes] = useState([]);
  const [projectInfo, setProjectInfo] = useState(projects ? projects : {});
  const [roofType, setRoofType] = useState({});
  const [projectPrice, setProjectPrice] = useState(0.0);
  const [successMessage, setSuccessMessage] = useState([]);
  const [errorMessage, setErrorMessage] = useState([]);
  const [showLoader, setShowLoader] = useState(false);
  const [showAlertBar, setShowAlertBar] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [isFormSaved, setIsFormSaved] = useState(false);
  const [addOpenModal, setAddOpenModal] = useState(false);
  const [addOpenProjecModal, setAddOpenProjecModal] = useState(false);
  const [openConfirmationBox, setOpenConfirmationBox] = useState(false);
  const { readOnly, userData, projectData, adminAccess, storeData } = useSelector(
    (store) => store.auth
  );

  const storeID = adminAccess
    ? storeData?.id
    : isUser(userData?.roles) || isAdmin(userData?.roles)
      ? userData?.storesList[0]?.storeId
      : null;

  // const [tempPDF, setTempPDF] = useState(null);
  const pdfComponentRef = useRef();

  const dispatch = useDispatch();

  const getAllroofTypes = async () => {
    const projectId = {
      projectId: projectID
    };

    if (!projectID || projectID == null || projectID == undefined) {
      navigate('/StoreInfo/projects');
    }
    try {
      setShowLoader(true);
      const roofSections = await authPostRequest('getRoofSectionsList', projectId);
      setShowLoader(false);
      if (!storeID) {
        navigate('/Stores');
      } else if (roofSections.resStatus === 0 && !projectData) {
        navigate('/Error');
      }
      const roofSectionList = roofSections.roofSectionsList;
      setRoofTypes(roofSectionList);
      setProjectPrice({
        projectBasePrice: roofSectionList[0]?.projectBasePrice,
        projectTotalPrice: roofSectionList[0]?.projectTotalPrice
      });
    } catch (err) {
      console.error(err);
      setProjectPrice(0.0);
    }
  };

  useEffect(() => {
    const visited = localStorage.getItem('visited');
    const isEditable = localStorage.getItem('isEditable');
    if (visited || isEditable == false) {
      setAddOpenModal(false);
    }
    if (visited && !isEditable) {
      setAddOpenModal(true);
    }

    localStorage.removeItem('isEditable');
    getAllroofTypes();
  }, [isFormSaved, projectData]);

  const sendPdfToMail = useCallback(async (pdf, pdfFileName, data) => {
    const jsonData = {
      projectId: projectID,
      billTo: userData?.userId,
      userId: userData?.userId,
      invoiceId: data?.invoiceId
    };
    const json = JSON.stringify(jsonData);
    const blob = new Blob([json], {
      type: 'application/json'
    });

    var formData = new FormData();
    formData.append('jsonData', blob);
    formData.append('file', new File([pdf], pdfFileName, { type: 'application/pdf' }));
    const invoiceEmailRes = await sendInvoiceViaEmail(pdf, formData, userData?.token);

    if (invoiceEmailRes?.resStatus === 1) {
      setSuccessMessage([invoiceEmailRes?.success]);
    } else {
      setErrorMessage([invoiceEmailRes?.error]);
    }
    setShowAlertBar(true);
    setShowLoader(false);
    setTimeout(() => window.scrollTo(0, 0), 0);
  }, []);

  //Handle confirmation for delete
  const handleConfirm = async () => {
    try {
      const params = {
        userId: userData.userId,
        roofSectionId: roofType.id
      };
      setShowLoader(true);
      setShowAlertBar(false);
      setIsFormSaved(false);
      const deleteRoof = await authPostRequest('deleteRoofSection', params);
      if (deleteRoof?.resStatus === 1) {
        setSuccessMessage([deleteRoof.resMessage]);
        window.scrollTo(0, 0);
        getAllroofTypes();
      } else {
        setErrorMessage([deleteRoof.resMessage]);
        window.scrollTo(0, 0);
      }

      setShowLoader(false);
      setIsFormSaved(true);
      setShowAlertBar(true);
      setOpenDialog(!openDialog);
    } catch (err) {
      setErrorMessage(['Error']);
      window.scrollTo(0, 0);
    }
  };

  //state set from delete roof section
  const handleDialogState = () => {
    setOpenDialog(!openDialog);
  };

  //for delete functionalities
  const handleDialogOpen = (roof) => {
    setRoofType(roof);
    setOpenDialog(true);
  };

  const handleEdit = (roof) => {
    setRoofType(roof);
    if (roof.quotationType == 'Snow Fence') {
      navigate('/StoreInfo/calculator-fence', {
        state: {
          roofData: roof,
          projectId: projectID,
          projectName: projects?.name,
          projectDescription: projectInfo?.description
        }
      });
    } else {
      navigate('/StoreInfo/calculator', {
        state: {
          roofData: roof,
          projectId: projectID,
          projectName: projects?.name,
          projectDescription: projectInfo?.description
        }
      });
    }
  };

  const createAndSendOrDownloadInvoicePDF = async (download = false) => {
    setSuccessMessage([]);
    setErrorMessage([]);
    setShowAlertBar(false);
    try {
      setShowLoader(true);
      // get fresh s3 URL for logo as it may have changes by admin in current login session
      const storeLogoFreshFromS3 = await getStoreLogoPreviewUrl(storeID);
      const invoiceRes = await authPostRequest('getInvoiceDetails', {
        projectId: projectID,
        billTo: userData?.userId,
        userId: userData?.userId
      });
      window.scrollTo(0, 0);

      if (invoiceRes.resStatus === 1) {
        const data = invoiceRes?.invoiceResponse[0];
        const invoiceData = getFormattedQuoteDataForPDF(data, userData, storeLogoFreshFromS3);

        const pdf = await generateInvoicePdf(
          <InvoicePDF invoiceData={invoiceData} ref={pdfComponentRef} />
        );

        // setTempPDF(<InvoicePDF invoiceData={invoiceData} ref={pdfComponentRef} />);

        const pdfFileName =
          invoiceData.store.name + '_Estimate_' + invoiceData.invoiceNumber + '.pdf';

        if (download) {
          downloadPDF(pdfFileName, invoiceData, pdfComponentRef);
          setShowLoader(false);
        } else {
          sendPdfToMail(pdf, pdfFileName, data);
        }
      } else {
        setErrorMessage([invoiceRes.resMessage]);
      }
    } catch (err) {
      console.log(err);
      window.scrollTo(0, 0);
      setShowLoader(false);
    }
  };

  const handleConfirmationOpen = (roof) => {
    setRoofType(roofTypes[roof]);
    setOpenConfirmationBox(true);
  };

  const handleDisableEnableRoofType = async () => {
    setSuccessMessage([]);
    setErrorMessage([]);
    try {
      const params = {
        userId: userData.userId,
        roofSectionId: roofType.id,
        enable: roofType.enable ? false : true,
        projectId: projectID
      };

      setShowLoader(true);
      setShowAlertBar(false);

      const roofStatus = await authPostRequest('enableDisableRoofSection', params);
      setShowLoader(false);
      if (roofStatus.resStatus === 1) {
        setIsFormSaved(!isFormSaved);
        setSuccessMessage([roofStatus.resMessage]);
      } else {
        setErrorMessage([roofStatus.resMessage]);
      }

      setShowAlertBar(true);
      setOpenConfirmationBox(!ConfirmationBox);
      window.scrollTo(0, 0);
    } catch (err) {
      setErrorMessage(['Error']);
      window.scrollTo(0, 0);
    }
  };

  return (
    <>
      <BackButton onClick={() => navigate('/StoreInfo/projects')} />
      <Grid
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        sx={{ marginTop: -1, marginBottom: -1 }}>
        <ProjectDetailsCard
          projectName={projectName}
          projectDescription={projectDescription}
          setAddOpenProjecModal={setAddOpenProjecModal}
        />
        <Grid display="flex">
          <PriceGrid>
            Price
            <span style={{ color: 'Black', fontWeight: 'bold', marginLeft: '1vh' }}>
              ${projectPrice?.projectTotalPrice?.toFixed(2) || (0.0).toFixed(2)}
            </span>
          </PriceGrid>
          <CustomButton
            add={true}
            type="submit"
            endIcon={<AddIcon />}
            onClick={() => {
              setAddOpenModal(true);
              dispatch(removeReadOnly());
            }}>
            Add
          </CustomButton>
        </Grid>
      </Grid>
      {showAlertBar && (
        <SuccessError
          successMessage={successMessage}
          errorMessage={errorMessage}
          setShowAlertBar={setShowAlertBar}
        />
      )}
      <Table
        data={roofTypes}
        options={tableOptionFilter}
        columns={[
          ...columns,
          {
            name: 'actions',
            label: 'Action',
            options: {
              filter: false,
              sort: false,
              customBodyRender: (value, tableMeta) => {
                return (
                  <Grid sx={{ ml: '-1.9vh' }}>
                    <Tooltip title="Delete">
                      <IconButton onClick={() => handleDialogOpen(roofTypes[tableMeta.rowIndex])}>
                        <DeleteForeverIcon style={{ color: '#800000' }} />
                      </IconButton>
                    </Tooltip>

                    {roofTypes[tableMeta.rowIndex]?.enable && (
                      <Tooltip title="Update">
                        <IconButton
                          onClick={() => {
                            handleEdit(roofTypes[tableMeta.rowIndex]);
                            readOnly && dispatch(removeReadOnly());
                          }}>
                          <EditIcon style={{ color: style.LIGHT.primary }} />
                        </IconButton>
                      </Tooltip>
                    )}

                    {!roofTypes[tableMeta.rowIndex]?.enable && (
                      <Tooltip title="View">
                        <IconButton
                          disabled={
                            typeof roofTypes[tableMeta.rowIndex]?.price === 'number'
                              ? !(roofTypes[tableMeta.rowIndex]?.price >= 0)
                              : true
                          }
                          onClick={() => {
                            handleEdit(roofTypes[tableMeta.rowIndex]);
                            dispatch(addReadOnly());
                          }}>
                          <RemoveRedEyeIcon
                            sx={{
                              color:
                                typeof roofTypes[tableMeta.rowIndex]?.price === 'number'
                                  ? roofTypes[tableMeta.rowIndex]?.price >= 0
                                    ? style.LIGHT.primary
                                    : 'gray'
                                  : 'gray'
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    )}

                    <Tooltip title="Download Quote">
                      <IconButton
                        onClick={() => {
                          roofTypes[tableMeta.rowIndex]?.price &&
                            downloadSingleQuotePDF(
                              tableMeta,
                              projectID,
                              userData,
                              storeID,
                              pdfComponentRef,
                              // setTempPDF,
                              setShowLoader
                            );
                        }}>
                        <SimCardDownloadIcon
                          sx={{
                            color: roofTypes[tableMeta.rowIndex]?.price
                              ? style.LIGHT.primary
                              : 'grey'
                          }}
                        />
                      </IconButton>
                    </Tooltip>

                    <Tooltip title={roofTypes[tableMeta.rowIndex]?.enable ? 'Enabled' : 'Disabled'}>
                      <IconButton
                        onClick={() => {
                          handleConfirmationOpen(tableMeta.rowIndex);
                        }}>
                        {roofTypes[tableMeta.rowIndex]?.enable ? (
                          <ToggleOnIcon style={{ color: 'green' }} />
                        ) : (
                          <ToggleOffIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                  </Grid>
                );
              }
            }
          }
        ]}
      />

      <Grid display="flex" direction="row" sx={{ justifyContent: 'flex-end' }}>
        <Grid xs={12} sx={{ mr: 2 }}>
          <CustomButton
            sx={{ mt: 2, mb: 0, mx: 0 }}
            type="button"
            add={true}
            endIcon={<Download />}
            disabled={
              !(roofTypes?.length > 0) || !roofTypes?.some((arr) => arr.price && arr.enable == true)
            }
            onClick={() => createAndSendOrDownloadInvoicePDF(true)}>
            Download Quote
          </CustomButton>
        </Grid>
        <Grid xs={12}>
          <CustomButton
            sx={{ mt: 2, mb: 0, mx: 0 }}
            type="button"
            endIcon={<EmailOutlined />}
            disabled={
              !(roofTypes?.length > 0) || !roofTypes?.some((arr) => arr.price && arr.enable == true)
            }
            onClick={() => createAndSendOrDownloadInvoicePDF(false)}>
            Email Quote
          </CustomButton>
        </Grid>
      </Grid>
      {showLoader && <Loader open={showLoader} />}
      {openDialog && (
        <DialogBox
          handleDialogBoxState={handleDialogState}
          openDialogBox={openDialog}
          handleConfirmAction={handleConfirm}
          deleteMessage={<DeleteSpan message="Roof Section" toBeDel={roofType.name} />}
        />
      )}
      {addOpenModal && (
        <CreateRoofSection
          openModal={addOpenModal}
          setOpenModal={setAddOpenModal}
          setSuccess={setSuccessMessage}
          setAlertBar={setShowAlertBar}
          setIsFormSaved={setIsFormSaved}
          isFormSaved={isFormSaved}
          projectId={projectID}
          projectName={projectName}
          projectDescription={projectDescription}
        />
      )}
      {addOpenProjecModal && (
        <CreateProject
          openModal={addOpenProjecModal}
          setOpenModal={setAddOpenProjecModal}
          setSuccess={setSuccessMessage}
          setAlertBar={setShowAlertBar}
          setIsFormSaved={setIsFormSaved}
          isFormSaved={isFormSaved}
          setProjectInf={setProjectInfo}
          project={projects}
          projectBasePrice={projectPrice?.projectBasePrice}
        />
      )}

      {openConfirmationBox && (
        <ConfirmationBox
          openConfirmationBox={openConfirmationBox}
          setOpenConfirmationBox={setOpenConfirmationBox}
          handleYes={() => handleDisableEnableRoofType(roofType?.active)}
          message={
            roofType?.enable ? (
              <TextHighlightSpan
                messageStart="If you disable the "
                messageEnd=", would not be part of project Quote. 
                            Are you sure you want to proceed?"
                spanText={roofType?.name}
              />
            ) : (
              <TextHighlightSpan
                messageStart="If you enable the "
                messageEnd=", would be part of project Quote. 
                            Are you sure you want to proceed?"
                spanText={roofType?.name}
              />
            )
          }
          title={roofType?.enable ? 'Disable?' : 'Enable?'}
        />
      )}

      {/* {tempPDF && tempPDF} */}
      <div id="reportDiv"></div>
    </>
  );
};

export default RoofSections;
